import { useQuery } from "@apollo/client";
import { FETCH_SUBSCRIPTION_INFO, FETCH_SUBSCRIPTION_ACCESS } from "@company/graphql/queries/fetchSubscriptionInfo";
import { SubscriptionType } from "@/app/graphql/codegen";
import { useIsAuthenticated } from "@auth/hooks/useIsAuthenticated";

export interface SubscriptionAccessInfo {
  hasActiveSubscription: boolean;
  hasAccess: boolean;
  subscriptionType: SubscriptionType;
  isInTrial: boolean;
  isInGracePeriod: boolean;
  isExpired: boolean;
  loading: boolean;
  error?: any;
}

/**
 * Enhanced hook for subscription access that uses the new state management logic
 * This replaces direct status === "ACTIVE" checks with proper business logic
 */
export function useSubscriptionAccess(): SubscriptionAccessInfo {
  // Determine if the user is already authenticated. We should not fire the
  // subscription access query before authentication is established, otherwise
  // we might briefly receive an "unauthenticated" response which causes UI
  // flicker (e.g. the subscription banner showing up shortly after login).
  const isAuthenticated = useIsAuthenticated();

  const { data: subscriptionData, loading, error } = useQuery(FETCH_SUBSCRIPTION_INFO, {
    errorPolicy: "ignore",
    skip: !isAuthenticated, // wait for auth before querying
  });

  // For backward compatibility, we also provide the old hasActiveSubscription logic
  // but the new hasAccess field should be preferred
  const hasActiveSubscription = subscriptionData?.subscriptionInfo?.hasAccess || false;

  return {
    hasActiveSubscription, // Legacy compatibility
    hasAccess: subscriptionData?.subscriptionInfo?.hasAccess || false,
    subscriptionType: subscriptionData?.subscriptionInfo?.subscriptionType || SubscriptionType.None,
    isInTrial: subscriptionData?.subscriptionInfo?.isInTrial || false,
    isInGracePeriod: subscriptionData?.subscriptionInfo?.isInGracePeriod || false,
    isExpired: subscriptionData?.subscriptionInfo?.isExpired || false,
    // We only consider the hook to be in loading state if the user is authenticated 
    // AND the GraphQL query itself is still loading. For unauthenticated users, we
    // can safely expose `loading: false` so that public routes (e.g. Onboarding)
    // are rendered immediately.
    loading: isAuthenticated ? loading : false,
    error,
  };
}

/**
 * Lightweight hook for just checking if user has active subscription
 * Uses the new hasActiveSubscription GraphQL query
 */
export function useHasActiveSubscription(): { hasActiveSubscription: boolean; loading: boolean; error?: any } {
  const { data, loading, error } = useQuery(FETCH_SUBSCRIPTION_ACCESS, {
    errorPolicy: "ignore",
  });

  return {
    hasActiveSubscription: data?.hasActiveSubscription || false,
    loading,
    error,
  };
}

/**
 * Helper function to determine if user should see upgrade prompts
 */
export function shouldShowUpgradePrompt(accessInfo: SubscriptionAccessInfo): boolean {
  // Don't show upgrade prompts if user has access
  if (accessInfo.hasAccess) {
    return false;
  }

  // Show upgrade prompts for expired, unpaid, or no subscriptions
  // but not during grace periods (user is still paying)
  return !accessInfo.isInGracePeriod;
}

/**
 * Helper function to get user-friendly subscription status text
 */
export function getSubscriptionStatusText(accessInfo: SubscriptionAccessInfo): string {
  if (accessInfo.loading) {
    return "Wird geladen...";
  }

  if (accessInfo.hasAccess) {
    if (accessInfo.isInTrial) {
      return "Testphase aktiv";
    }
    if (accessInfo.isInGracePeriod) {
      return "Aktiv (Kulanzfrist)";
    }
    return "Aktiv";
  }

  if (accessInfo.isExpired) {
    return "Abgelaufen";
  }

  return "Inaktiv";
} 